import { RouteLocationNormalized, RouteMeta } from 'vue-router';
import { BlogArticleMetadata, DocumentMetadata, TutorialMetadata } from '@bifrost-dev-docs-mfe/common/types/api';
import { Composer } from 'vue-i18n';
import {
  ApplicationDescription,
  ApplicationTitle,
  DeutscheTelekomVendorId,
  DevPortalURL,
  DevPortalURLDeutscheTelekom,
  isVonageBrand,
  TwitterCardType,
  VonageVendorId,
} from '@/constants';
import { setCanonicalUrl, updateMetaTags } from '@/utils/meta';
import { OpenGraphPageType, setApplicationOGTags, setPageOGTags } from '@/utils/openGraph';
import client from '@/api/client';

export function setApplicationMetaTags() {
  // set static tags
  updateMetaTags({
    description: { content: ApplicationDescription },
    'twitter:card': { content: TwitterCardType },
  });
  // set application specific Open Graph tags
  setApplicationOGTags();
}

async function getMetadata(
  i18n: Composer,
  { meta, params: { id, app } }: RouteLocationNormalized
): Promise<BlogArticleMetadata | DocumentMetadata | TutorialMetadata> {
  const { ogType = OpenGraphPageType.Generic, type = '' } = meta as RouteMeta;
  let pathId = '';
  if (Array.isArray(id)) {
    pathId = id.join('/').toString();
  } else if (typeof id as string) {
    pathId = id;
  }
  switch (ogType) {
    case OpenGraphPageType.Document:
      return client.getDocumentMetadata(type, i18n?.locale.value, app ? `/${app}/${pathId}` : pathId);
    case OpenGraphPageType.Tutorial:
      return client.getTutorialMetadata(i18n?.locale.value, pathId);
    case OpenGraphPageType.ApiSpec:
      return client.getAPISpecMetaData(pathId);
    default:
      return {};
  }
}

export async function computeMetaTitle(
  title: string | undefined,
  routeMetaTitle: string | undefined,
  metaTitle: string | undefined
): Promise<string | undefined> {
  /**
   * As per the comment in the ticket if there is no metaTitle then return Title
   * https://jira.vonage.com/browse/APIDP-471
   * */
  if (metaTitle) return metaTitle;
  if (title) return title;
  return routeMetaTitle;
}

export async function setPageTagsForRoute(i18n: Composer, route: RouteLocationNormalized) {
  const routeMeta = route.meta || {};
  const { ogType = OpenGraphPageType.Generic } = routeMeta;
  const metadata = await getMetadata(i18n, route);
  const metaTitle = await computeMetaTitle(
    metadata?.title,
    routeMeta.title ? i18n.t(routeMeta.title) : undefined,
    metadata.metaTitle
  );
  const pageTitle = metaTitle || ApplicationTitle;
  /**
   * if there is no metaDescription then it should choose metaDescription as ApplicationDescription as per the ticket
   * https://jira.vonage.com/browse/APIDP-472
   * */
  const pageDescription = metadata?.metaDescription || ApplicationDescription;
  metadata.description = pageDescription;
  await setPageOGTags(ogType, pageTitle, metadata);
  document.title = pageTitle;
  updateMetaTags({ description: { content: pageDescription } });
  /**
   *   if noIndex is specified, We need to apply the noindex tag irrespective of any brand
   *   if brand is specified, then we need to set robots tag to 'all' for that particular brand and rest brands will be noindexed.
   */
  if (metadata.noIndex) {
    updateMetaTags({ robots: { content: 'noindex' } });
  } else if (metadata.brand) {
    const robotsMetaTag = metadata.brand === window.branding.vendorId ? 'all' : 'noindex';
    updateMetaTags({ robots: { content: robotsMetaTag } });
  } else {
    const robotsMetaTag = window.branding.vendorId === VonageVendorId && !metadata.noIndex ? 'all' : 'noindex';
    updateMetaTags({ robots: { content: robotsMetaTag } });
  }

  /**
   * if brand mentioned is deustche-telekom then we need to set DT url as canonical for Vonage page
   * */
  if (metadata.brand) {
    if (metadata.brand === DeutscheTelekomVendorId) {
      setCanonicalUrl(new URL(window.location.pathname, DevPortalURLDeutscheTelekom).href);
    } else if (metadata.brand === VonageVendorId) {
      setCanonicalUrl(new URL(window.location.pathname, DevPortalURL).href);
    }
  } else if (!isVonageBrand) {
    setCanonicalUrl(new URL(window.location.pathname, DevPortalURL).href);
  }
}
